import {
  FilterDefinition,
  FilterMap,
  StringHelper,
  buildFilterOptions,
  columnFilterFn,
  deserializeFilterQueryParamValues,
  filterItems,
  serializeFilterQueryParamValues
} from "@swivl-mono/helpers";
import { UnitInterface, UserInterface, UserVarKey, UserVarsMap } from "@swivl-mono/types";
import { DataTable, DataTableVariant, DropdownPosition, DropdownVariant, FilterDropdown } from "@swivl-mono/ui";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { ActiveFilterChipSection } from "../../../../../Components/ActiveFilterChipSection/ActiveFilterChipSection";
import { DataCardLocation } from "../../../../../Components/DataCards/DataCard.Location";
import Pill, { PillSize, PillStyle, PillType } from "../../../../../Components/Pill/Pill";
import { useUrlState } from "../../../../../Hooks/useUrlState";
import { AlertsModel } from "../../../../../Models/Notifications/Alerts.Model";
import { UnitsModel } from "../../../../../Models/Units/Units.Model";
import UserVarsModel from "../../../../../Models/Variables/UserVars.Model";
import { DataCardFooter } from "../Components/DataCardFooter";

interface UserDetailsLocationDetailsViewProps {
  navigateForward: (tab?: string) => void;
  user: UserInterface;
  userVars: UserVarsMap | undefined;
}

export function UserDetailsLocationDetailsView({ navigateForward, user, userVars }: UserDetailsLocationDetailsViewProps) {
  const { searchParams, setMultipleSearchParams } = useUrlState();
  const locationId = Number(searchParams.get("locationId"));
  const prevLocationId = useRef(locationId);
  const { unitsByLocation } = UnitsModel.useState((s) => s);
  const [filteredUnits, setFilteredUnits] = useState<UnitInterface[]>(unitsByLocation?.[locationId] || []);

  const filterDefinitions: FilterDefinition<UnitInterface>[] = [
    {
      id: "sizes",
      label: "Sizes",
      getValues: (items) => items.map((item) => item.dimensions && StringHelper.normalizeUnitDimensions(item.dimensions)),
      formatValue: (value) => value.replace("x", " x "),
      sort: (a, b) => {
        const [aWidth, aHeight] = a.split("x").map((dimension) => Number(dimension.trim()));
        const [bWidth, bHeight] = b.split("x").map((dimension) => Number(dimension.trim()));
        return aWidth - bWidth || aHeight - bHeight;
      }
    },
    {
      id: "floor",
      label: "Floor",
      getValues: (items) => items.map((item) => item.floor),
      sort: (a, b) => Number(a) - Number(b)
    },
    {
      id: "amenities",
      label: "Amenities",
      getValues: (items) => items.flatMap((item) => item.features?.map((amenity) => amenity) || []),
      sort: (a, b) => a.localeCompare(b)
    }
  ];

  const filterParamKeys = useMemo(() => {
    return filterDefinitions.map((definition) => definition.id);
  }, []);

  const initialFilterOptions = useMemo(() => {
    if (!unitsByLocation || !locationId) return [];
    const units = unitsByLocation?.[locationId];

    return buildFilterOptions<UnitInterface>(units, filterDefinitions);
  }, [unitsByLocation, locationId]);

  const selectedFiltersByGroup = useMemo(() => {
    const selections: Record<string, string[]> = {};

    for (const groupId of filterParamKeys) {
      const paramValue = searchParams.get(groupId);
      if (!paramValue) {
        continue;
      }

      selections[groupId] = deserializeFilterQueryParamValues(paramValue);
    }

    return selections;
  }, [filterParamKeys, searchParams]);

  const handleChangeFilters = (groupId: string, filters: string[]) => {
    const updates: Record<string, string | undefined> = {};
    updates[groupId] = filters.length > 0 ? serializeFilterQueryParamValues(filters) : undefined;
    setMultipleSearchParams(updates);
  };

  const handleClearAllFilters = () => {
    const updates: Record<string, string | undefined> = {};
    for (const key of filterParamKeys) {
      updates[key] = undefined;
    }
    setMultipleSearchParams(updates);
  };

  const columnFilters = useMemo(() => {
    return Object.keys(selectedFiltersByGroup)
      .filter((key) => selectedFiltersByGroup[key]?.length > 0)
      .map((key) => ({
        id: key,
        value: selectedFiltersByGroup[key]
      }));
  }, [selectedFiltersByGroup]);

  const unitFilterMap: FilterMap<UnitInterface> = {
    sizes: (unit, values) => values.includes(unit.dimensions ? StringHelper.normalizeUnitDimensions(unit.dimensions) : undefined),
    floor: (unit, values) => values.includes(String(unit.floor)),
    amenities: (unit, values) => unit.features?.some((feature) => values.includes(feature)) ?? false
  };

  // handle filtering units
  useEffect(() => {
    const units = unitsByLocation?.[locationId] || [];
    const filteredItems = filterItems(units, columnFilters, unitFilterMap);

    setFilteredUnits(filteredItems);
  }, [columnFilters, unitsByLocation, locationId]);

  const columns = useMemo(
    () => [
      {
        header: "Size",
        accessorKey: "dimensions",
        filterFn: columnFilterFn
      },
      {
        header: "Floor",
        accessorKey: "floor",
        filterFn: columnFilterFn
      },
      {
        header: "Price",
        accessorKey: "rate",
        filterFn: columnFilterFn
      },
      {
        header: "Amenities",
        accessorKey: "features",
        filterFn: columnFilterFn,
        cell: (info: any) => {
          const features = info.getValue() as string[];
          if (features.length === 0) return "--";

          return features.join(", ");
        }
      }
    ],
    []
  );

  const handleNavigateForward = useCallback(
    (tab: string, unitId: number) => {
      setMultipleSearchParams({
        locationId: searchParams.get("locationId") || "",
        unitId: String(unitId),
        tab
      });

      navigateForward(tab);
    },
    [searchParams, navigateForward, setMultipleSearchParams]
  );

  useEffect(() => {
    if (locationId && !unitsByLocation?.[locationId]) {
      UnitsModel.getUnitsByLocation(locationId);
    }
  }, [locationId]);

  // reset filter when locationid changes
  useEffect(() => {
    if (prevLocationId.current !== locationId) {
      prevLocationId.current = locationId;

      const updates: Record<string, string | undefined> = {};
      for (const key of filterParamKeys) {
        updates[key] = undefined;
      }
      setMultipleSearchParams(updates);
    }
  }, [filterParamKeys, locationId, setMultipleSearchParams]);

  const handleViewFullDetails = () => {
    AlertsModel.errorToast("This feature is not available yet.");
    return;
  };

  const handleSetStore = () => {
    UserVarsModel.saveUserVars(user.id, {
      Location: locationId
    });
    return;
  };

  return (
    <div className="flex h-full flex-col gap-2">
      <div className="mb-4">
        <DataCardLocation
          title="Prospect Profile"
          locationId={locationId}
          pills={
            Number(userVars?.[UserVarKey.Location]) === locationId && [
              <Pill key={`pill-${locationId}`} type={PillType.ORANGE} size={PillSize.SMALL} style={PillStyle.DARK} text="Selected"></Pill>
            ]
          }
          size="full"
          footer={
            <DataCardFooter
              handleSetStore={handleSetStore}
              handleViewFullDetails={handleViewFullDetails}
              locationId={locationId}
              userVars={userVars}
            />
          }
        />
      </div>

      <div className="flex items-center gap-2 px-1">
        <h2 className="py-2 text-left font-sans text-[15px] font-semibold text-black">Available Units</h2>
        <FilterDropdown
          id="unit-filters"
          filterOptions={initialFilterOptions}
          selectedFilters={selectedFiltersByGroup}
          onChangeFilters={handleChangeFilters}
          onClearAllFilters={handleClearAllFilters}
          position={DropdownPosition.RIGHT}
        />
      </div>
      <ActiveFilterChipSection
        classNames={{ wrapper: "shrink-0 rounded-sm border border-grey-300" }}
        filterOptions={initialFilterOptions}
        selectedFiltersByGroup={selectedFiltersByGroup}
        handleClearFilter={(groupId, value) => {
          if (!value) {
            handleChangeFilters(groupId, []);
            return;
          }

          const currentFilters = selectedFiltersByGroup[groupId] ?? [];
          const nextFilters = currentFilters.filter((filterValue) => filterValue !== value);
          handleChangeFilters(groupId, nextFilters);
        }}
        handleClearFilters={handleClearAllFilters}
        handleSetFilters={handleChangeFilters}
      />
      <div className="min-h-100 overflow-auto">
        <DataTable
          areRowsSelectable
          handleSelectCell={(cell) => {
            handleNavigateForward("Unit Details", cell);
          }}
          data={filteredUnits}
          columns={columns}
          manualConfig={{ manualFiltering: true }}
          columnFilterOptions={columnFilters}
          variant={DataTableVariant.SECONDARY}
          pinFooter
        />
      </div>
    </div>
  );
}
